import { GroupPricing, PackagePricing, StayType } from '../types';

const STAY_TIER_KEYS = ['standard', 'deluxe'] as const;

export function isStayTierPricing(pricing: PackagePricing | undefined): pricing is { standard?: GroupPricing; deluxe?: GroupPricing } {
  if (!pricing) return false;
  return STAY_TIER_KEYS.some((key) => key in pricing);
}

export function hasStayTypeSelector(pricing: PackagePricing | undefined): boolean {
  if (!isStayTierPricing(pricing)) return false;
  return Boolean(pricing.standard && pricing.deluxe);
}

function getTierPricing(pricing: PackagePricing, stayType?: StayType): GroupPricing | undefined {
  if (isStayTierPricing(pricing)) {
    if (stayType) return pricing[stayType];
    return pricing.standard ?? pricing.deluxe;
  }
  return pricing;
}

function parseGroupSizes(groupPricing: GroupPricing | undefined): number[] {
  if (!groupPricing) return [];
  return Object.keys(groupPricing)
    .map(Number)
    .filter((size) => !isNaN(size))
    .sort((a, b) => a - b);
}

export function getAvailableGroupSizes(pricing: PackagePricing | undefined, stayType?: StayType): number[] {
  if (!pricing) return [2];
  const sizes = parseGroupSizes(getTierPricing(pricing, stayType));
  return sizes.length > 0 ? sizes : [2];
}

export function getMinimumGroupSize(pricing: PackagePricing | undefined, stayType?: StayType): number {
  const sizes = getAvailableGroupSizes(pricing, stayType);
  return sizes[0];
}

export function getPricePerPerson(
  pricing: PackagePricing | undefined,
  groupSize: number,
  stayType?: StayType
): number {
  if (!pricing) return 0;
  const tierPricing = getTierPricing(pricing, stayType);
  const price = tierPricing?.[groupSize.toString()];
  return price && !isNaN(price) ? price : 0;
}

export function getTotalPrice(
  pricing: PackagePricing | undefined,
  groupSize: number,
  stayType?: StayType
): number {
  const pricePerPerson = getPricePerPerson(pricing, groupSize, stayType);
  return pricePerPerson * groupSize;
}

export function getDiscountPercentage(
  pricing: PackagePricing | undefined,
  groupSize: number,
  stayType?: StayType
): number {
  const minGroupSize = getMinimumGroupSize(pricing, stayType);
  const currentPrice = getPricePerPerson(pricing, groupSize, stayType);
  const basePrice = getPricePerPerson(pricing, minGroupSize, stayType);

  if (!currentPrice || !basePrice) return 0;
  return Math.round(((basePrice - currentPrice) / basePrice) * 100);
}

export function getMinimumListingPrice(pricing: PackagePricing | undefined): {
  price: number;
  groupSize: number;
} {
  if (!pricing) return { price: 0, groupSize: 2 };

  if (isStayTierPricing(pricing)) {
    let minPrice = Infinity;
    let minGroupSize = 2;

    for (const tier of STAY_TIER_KEYS) {
      const tierPricing = pricing[tier];
      if (!tierPricing) continue;

      for (const size of parseGroupSizes(tierPricing)) {
        const price = getPricePerPerson(pricing, size, tier);
        if (price > 0 && price < minPrice) {
          minPrice = price;
          minGroupSize = size;
        }
      }
    }

    return minPrice === Infinity ? { price: 0, groupSize: 2 } : { price: minPrice, groupSize: minGroupSize };
  }

  const groupSize = getMinimumGroupSize(pricing);
  return { price: getPricePerPerson(pricing, groupSize), groupSize };
}
